import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, ListItem, Autocomplete } from "@mui/material";
import moment from "moment";
import CustomDialog from "../../components/layouts/common/CustomDialog";
import CustomInput from "../../components/inputs/CustomInputs";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import { StyledSearchBar } from "../../components/inputs/SearchBar";
import { fetchCategoryApi } from "../../apis/category.api";
import { fetchRawMaterialApi } from "../../apis/rawMaterial.api";
import { FetchSKDApi } from "../../apis/skd.api";
import { FetchSFGApi } from "../../apis/sfg.api";
import { FetchFGApi } from "../../apis/FG.api";
import { callSnackBar } from "../../store/actions/snackbarAction";
import { SNACK_BAR_VARIETNS, MATERIAL_TYPE } from "../../utils/constants";
import { titleCase } from "../../utils/main";
import { CreateDirectPurchaseRequestApi } from "../../apis/purchaseRequest.api";

const DirectPurchaseRequestController = ({
  onClose = () => {},
  callBack = () => {},
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState({
    err: "",
    date: moment().toISOString(),
    categoryId: null,
    categoryName: null,
    materialType: null,
    materialId: null,
    materialName: null,
    quantity: "",
    reason: "",
    link: "",
  });

  const materialTypes = useMemo(
    () =>
      Object.keys(MATERIAL_TYPE).map((key) => ({
        label: titleCase(key),
        _id: MATERIAL_TYPE[key],
      })),
    [],
  );

  const validate = () => {
    if (!fields.date) return "Date is required";
    if (!fields.categoryId) return "Category is required";
    if (!fields.materialType) return "Material Type is required";
    if (!fields.materialId) return "Material Name is required";
    if (!fields.quantity) return "Quantity is required";
    if (!fields.reason) return "Reason is required";
    return true;
  };

  const getMaterialFetchFunction = () => {
    switch (fields.materialType) {
      case MATERIAL_TYPE.RAW_MATERIALS:
        return fetchRawMaterialApi;
      case MATERIAL_TYPE.SKD:
        return FetchSKDApi;
      case MATERIAL_TYPE.SFG:
        return FetchSFGApi;
      case MATERIAL_TYPE.FG:
        return FetchFGApi;
      default:
        return null;
    }
  };

  const buildMaterialValue = () => {
    if (!fields.materialId) return null;
    return {
      _id: fields.materialId,
      name: fields.materialName,
    };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const validation = validate();
    if (validation !== true) {
      setFields({ ...fields, err: validation });
      return;
    }

    setLoading(true);
    try {
      await CreateDirectPurchaseRequestApi({
        categoryId: fields.categoryId,
        materialType: fields.materialType,
        materialId: fields.materialId,
        quantity: Number(fields.quantity),
        reason: fields.reason,
        link: fields.link,
        date: fields.date,
      });
      dispatch(
        callSnackBar(
          "Direct purchase request created successfully",
          SNACK_BAR_VARIETNS.suceess,
        ),
      );
      callBack();
      onClose();
    } catch (error) {
      dispatch(
        callSnackBar(
          error?.message || "Failed to create direct purchase request",
          SNACK_BAR_VARIETNS.error,
        ),
      );
      setFields({
        ...fields,
        err: error?.message || "Failed to create direct purchase request",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomDialog
      id="direct-purchase"
      loading={loading}
      err={fields.err}
      title="Direct Purchase"
      closeText="Close"
      confirmText="Create Direct Purchase"
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <Box sx={{ width: "100%" }}>
          <AsyncDropDown
            defaultVal={
              fields.categoryId
                ? {
                    _id: fields.categoryId,
                    name: fields.categoryName,
                  }
                : null
            }
            lazyFun={async (para) =>
              await fetchCategoryApi({ ...para, allStatus: true })
            }
            OptionComponent={({ option, ...rest }) => {
              return <ListItem {...rest}>{option.name}</ListItem>;
            }}
            value={fields.categoryId}
            onChange={async (changedVal) =>
              setFields({
                ...fields,
                categoryId: changedVal ? changedVal._id : null,
                categoryName: changedVal ? changedVal.name : null,
              })
            }
            titleKey="name"
            valueKey="_id"
            InputComponent={(params) => (
              <StyledSearchBar
                placeholder="Select Category*"
                {...params}
                margin="none"
              />
            )}
          />
        </Box>

        <Autocomplete
          disableClearable
          options={materialTypes}
          value={
            materialTypes.find((item) => item._id === fields.materialType) ||
            null
          }
          onChange={(e, newVal) =>
            setFields({
              ...fields,
              materialType: newVal ? newVal._id : null,
              materialId: null,
              materialName: null,
            })
          }
          sx={{ width: "100%" }}
          renderInput={(params) => (
            <CustomInput
              placeholder="Select Material Type*"
              {...params}
              label="Select Material Type*"
              margin="dense"
            />
          )}
        />

        {fields.materialType ? (
          <AsyncDropDown
            Value={buildMaterialValue()}
            lazyFun={async (para) => {
              const fetchFn = getMaterialFetchFunction();
              return fetchFn
                ? await fetchFn({ ...para, allStatus: true })
                : { result: [] };
            }}
            OptionComponent={({ option, ...rest }) => (
              <ListItem {...rest}>
                {option?.name} {option?.mpn ? `[${option?.mpn}]` : ""}
              </ListItem>
            )}
            onChange={async (changedVal) =>
              setFields({
                ...fields,
                materialId: changedVal ? changedVal._id : null,
                materialName: changedVal
                  ? `${changedVal?.name ?? ""}${changedVal?.mpn ? ` [${changedVal?.mpn}]` : ""}`.trim()
                  : null,
              })
            }
            titleKey="name"
            valueKey="_id"
            InputComponent={(params) => (
              <CustomInput
                {...params}
                label="Select Material*"
                placeholder="Select Material"
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        ) : null}

        <CustomInput
          value={fields.quantity}
          onChange={(e) =>
            setFields({ ...fields, quantity: e.target.value, err: "" })
          }
          type="number"
          label="Quantity *"
          min={0}
        />

        <CustomInput
          value={fields.reason}
          onChange={(e) =>
            setFields({ ...fields, reason: e.target.value, err: "" })
          }
          label="Reason *"
        />

        <CustomInput
          value={fields.link}
          onChange={(e) => setFields({ ...fields, link: e.target.value })}
          label="Link"
        />
      </Box>
    </CustomDialog>
  );
};

export default DirectPurchaseRequestController;
